import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import notifications from "../../../common/Components/Notifications/Notification";
import { getNotCounted, saveQuickRate, setPersonDayRate } from "../../../services/karkhanaApi";
import { useKT } from "../i18n/kt";

import "../people/quick-add.scss";

type Fix =
  | { kind: "loom"; designId: string; designNumber: string; workTypeId: string; part: string; from: string; inches: string }
  | { kind: "time"; personId: string; who: string; from: string };

/**
 * Work that can't be paid yet because a rate is missing. Work is never blocked: it is saved, listed
 * here, and paid the moment the owner fills the rate (from the first unpaid day).
 */
const NotCounted = ({ onChanged }: { onChanged?: () => void }) => {
  const { t, lang } = useKT();
  // "24 Oct": the year only makes the line wrap.
  const fmtDay = (iso?: string) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", { day: "numeric", month: "short" }) : "");
  const location = useLocation();
  const [data, setData] = useState<any>(null);
  const [fix, setFix] = useState<Fix | null>(null);
  const [rate, setRate] = useState("");
  const [unit, setUnit] = useState<"INCH" | "GIRAH">("INCH");
  const [from, setFrom] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLElement>(null);

  const load = useCallback(() => {
    getNotCounted()
      .then((res: any) => setData(res?.success ? res.data : null))
      .catch(() => setData(null));
  }, []);

  useEffect(load, [load]);

  useEffect(() => {
    if (data && location.hash === "#not-counted") ref.current?.scrollIntoView({ block: "start" });
  }, [data, location.hash]);

  const loom: any[] = data?.loom || [];
  const time: any[] = data?.time || [];
  if (!loom.length && !time.length) return null;
  const canFix = !!data?.canFix;

  const open = (f: Fix) => {
    setFix(f);
    setRate("");
    setUnit("INCH");
    setFrom(f.from);
  };

  const save = async () => {
    if (!fix || !rate.trim()) return;
    setBusy(true);
    try {
      const res: any =
        fix.kind === "loom"
          ? await saveQuickRate({ designId: fix.designId, workTypeId: fix.workTypeId, rate: rate.trim(), rateUnit: unit, effectiveFrom: from })
          : await setPersonDayRate(fix.personId, rate.trim(), from);
      if (!res?.success) {
        notifications({ title: t("Not saved"), message: t(res?.message || "Something went wrong"), type: "danger" });
        return;
      }
      notifications({
        title: t("Rate saved"),
        message:
          fix.kind === "loom"
            ? t("{{n}} entries are now paid", { n: res.data?.repriced ?? 0 })
            : t("{{who}}'s days are now paid", { who: fix.who }),
        type: "success",
      });
      setFix(null);
      load();
      onChanged?.();
    } catch {
      notifications({ title: t("No signal"), message: t("This needs a connection. Try again in a moment."), type: "danger" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="not-counted" ref={ref} className="knc">
      <h3>
        {t("Not counted yet")} · {loom.length + time.length}
      </h3>
      <p>{canFix ? t("Work is saved. It is paid once the rate is filled.") : t("Work is saved. The owner has been told.")}</p>
      {loom.map((g) => (
        <div key={`${g.designId}|${g.workTypeId}`} className="knc__row">
          <span>
            <strong>
              {g.designNumber} · {t(g.workTypeName || g.workTypeCode || "")}
            </strong>
            <small>
              {(g.weavers || []).slice(0, 3).join(", ")} · {g.inches} in · {t("since {{day}}", { day: fmtDay(g.firstDate) })}
            </small>
          </span>
          {canFix ? (
            <button
              type="button"
              className="knc__btn"
              onClick={() =>
                open({
                  kind: "loom",
                  designId: g.designId,
                  designNumber: g.designNumber,
                  workTypeId: g.workTypeId,
                  part: g.workTypeName || g.workTypeCode,
                  from: g.firstDate,
                  inches: g.inches,
                })
              }
            >
              {t("Fill rate")}
            </button>
          ) : null}
        </div>
      ))}
      {time.map((p) => (
        <div key={p.id} className="knc__row">
          <span>
            <strong>
              <span className="kw-code">{p.code}</span> {p.name} · {t("day rate")}
            </strong>
            <small>{t(p.days === 1 ? "1 day without a rate · since {{day}}" : "{{n}} days without a rate · since {{day}}", { n: p.days, day: fmtDay(p.firstDate) })}</small>
          </span>
          {canFix ? (
            <button type="button" className="knc__btn" onClick={() => open({ kind: "time", personId: p.id, who: p.name, from: p.firstDate })}>
              {t("Set ₹/day")}
            </button>
          ) : null}
        </div>
      ))}

      {fix ? (
        <>
          <div className="kq-backdrop" onClick={busy ? undefined : () => setFix(null)} />
          <section className="kq" role="dialog" aria-modal="true">
            <div className="kq__grab" aria-hidden="true" />
            <h3>{fix.kind === "loom" ? t("Rate for {{design}}", { design: fix.designNumber }) : t("Day rate for {{who}}", { who: fix.who })}</h3>
            <label className="kq__label" htmlFor="knc-rate">
              {fix.kind === "loom" ? `${t(fix.part)} · ${t("weaver")}` : t("Pay per day")}
            </label>
            <input
              id="knc-rate"
              className="kq__input"
              value={rate}
              autoFocus
              onChange={(e) => setRate(e.target.value.replace(/[^\d./]/g, ""))}
              inputMode="decimal"
              placeholder="₹"
            />
            {fix.kind === "loom" ? (
              <div className="kq__chips">
                {(["INCH", "GIRAH"] as const).map((u) => (
                  <button key={u} type="button" className={`kr-choice${unit === u ? " is-on" : ""}`} onClick={() => setUnit(u)}>
                    {u === "INCH" ? t("per inch") : t("per girah")}
                  </button>
                ))}
              </div>
            ) : null}
            <label className="kq__label" htmlFor="knc-from">
              {t("Applies from")} <i>{t("first unpaid day")}</i>
            </label>
            <input id="knc-from" type="date" className="kq__input" value={from} onChange={(e) => setFrom(e.target.value || fix.from)} />
            {fix.kind === "loom" && rate && Number(rate) > 0 && unit === "INCH" ? (
              <div className="knc__money">
                <span>
                  {fix.inches} in × ₹{rate}
                </span>
                <strong>₹{Math.round(Number(fix.inches) * Number(rate)).toLocaleString("en-IN")}</strong>
              </div>
            ) : null}
            <p className="kq__note">
              {fix.kind === "loom"
                ? t("Other parts and the incentive can be added later in Rate cards. Only the owner approves rates.")
                : t("Hourly pay, overtime and per-activity rates are in Time rates.")}
            </p>
            <div className="kq__foot">
              <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={() => setFix(null)}>
                {t("Later")}
              </button>
              <button type="button" className="btn ResetBtn" disabled={busy || !rate.trim()} onClick={save}>
                {busy ? t("Saving…") : t("Save & approve")}
              </button>
            </div>
          </section>
        </>
      ) : null}
    </section>
  );
};

export default NotCounted;
